/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  ACTIONS,
  BannerViewParams,
  BannerTapParams,
  CHINA_DISCLAIMER_VIEWED,
  LAST_SEARCHED_CLICKED,
  LAST_SEARCHED_VIEWED,
  LINE_OF_BUSINESS,
  SCREEN_NAME,
  SNAP_BANNER,
  SNAP_SEARCH_WIDGET,
  SnapChinaDisclaimerViewedParams,
  SnapLastSearchedClickedParams,
  SnapLastSearchedViewedParams,
  SnapSearchButtonParams,
  TripTypeTabParams,
  EVENT_NAMES,
  SNAP_SEARCH_WIDGET_USW,
} from './snapSearchWidget.meta';

/**
 * SNAP Banner Analytics
 */

/**
 * Build analytics parameters for SNAP banner view event
 * @param params - Banner view parameters (bannerName, position)
 * @returns Analytics parameters object
 */
export const buildSnapBannerViewAnalytics = (
  params: BannerViewParams,
): Record<string, string | number> => {
  const label = `${params.bannerName}|${params.position}`;

  return {
    action: ACTIONS.VIEW,
    category: SNAP_BANNER.CATEGORY,
    label,
    lineofbusiness: LINE_OF_BUSINESS.snap,
    screenName: SCREEN_NAME.snapSearchPage,
  };
};

/**
 * Build analytics parameters for SNAP banner tap event
 * @param params - Banner tap parameters (bannerName, position)
 * @returns Analytics parameters object
 */
export const buildSnapBannerTapAnalytics = (
  params: BannerTapParams,
): Record<string, string | number> => {
  const label = `${params.bannerName}|${params.position}`;

  return {
    action: ACTIONS.TAP,
    category: SNAP_BANNER.CATEGORY,
    label,
    lineofbusiness: LINE_OF_BUSINESS.snap,
    screenName: SCREEN_NAME.snapSearchPage,
  };
};

/**
 * Trip Type Tab Analytics
 */

/**
 * Build analytics parameters for trip type tab tap event
 * @param params - Trip type tab parameters (tripType)
 * @returns Analytics parameters object
 */
export const buildTripTypeTabAnalytics = (
  params: TripTypeTabParams,
): Record<string, string | number> => {
  return {
    action: ACTIONS.TAP,
    category: SNAP_SEARCH_WIDGET.CATEGORY,
    label: params.tripType,
    lineofbusiness: LINE_OF_BUSINESS.snap,
    screenName: SCREEN_NAME.snapSearchPage,
  };
};

/**
 * Search Widget Analytics
 */

/**
 * Build analytics parameters for SNAP search widget view event
 * @returns Analytics parameters object
 */
export const buildSnapSearchWidgetViewAnalytics = (): Record<
  string,
  string | number
> => {
  return {
    action: EVENT_NAMES.SEARCH_WIDGET_VIEWED,
    category: SNAP_SEARCH_WIDGET_USW.CATEGORY,
    label: SNAP_SEARCH_WIDGET_USW.LABELS,
    lineofbusiness: LINE_OF_BUSINESS.snap,
    screenName: SCREEN_NAME.snapSearchPage,
  };
};

/**
 * Build analytics parameters for SNAP search button click event
 * @param params - Search button click parameters
 * @returns Analytics parameters object
 */
export const buildSnapSearchButtonClickAnalytics = (
  params: SnapSearchButtonParams,
): Record<string, string | number> => {
  return {
    action: EVENT_NAMES.SEARCH_BUTTON_CLICKED,
    departdate: params.departdate,
    returndate: params.flighttype === 'RT' ? params.returndate : '',
    checkindate: params.checkindate,
    checkoutdate: params.checkoutdate,
    currency: params.currency,
    originairport: params.originairport,
    destinationairport: params.destinationairport,
    origincountry: params.origincountry,
    destinationcountry: params.destinationcountry,
    journeytype: params.journeytype,
    flighttype: params.flighttype,
    rooms: params.rooms,
    totalpax: params.totalpax,
    lengthofstay: params.lengthofstay,
    adult: params.adult,
    kid: params.kid,
    infant: params.infant,
    coupon: params.coupon,
    screenName: SCREEN_NAME.snapSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.snap,
  };
};

/**
 * Build analytics parameters for SNAP last searched viewed event
 * @param params - Last searched viewed parameters
 * @returns Analytics parameters object
 */
export const buildSnapLastSearchedViewedAnalytics = (
  params: SnapLastSearchedViewedParams,
): Record<string, string | number> => {
  const label = `${params.flightRoute}|${params.position}`;

  return {
    category: LAST_SEARCHED_VIEWED.CATEGORY,
    action: LAST_SEARCHED_VIEWED.ACTION,
    label,
    screenName: SCREEN_NAME.snapSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.snap,
  };
};

/**
 * Build analytics parameters for SNAP last searched clicked event
 * @param params - Last searched clicked parameters
 * @returns Analytics parameters object
 */
export const buildSnapLastSearchedClickedAnalytics = (
  params: SnapLastSearchedClickedParams,
): Record<string, string | number> => {
  const label = `${params.flightRoute}|${params.position}`;

  return {
    category: LAST_SEARCHED_CLICKED.CATEGORY,
    action: LAST_SEARCHED_CLICKED.ACTION,
    label,
    screenName: SCREEN_NAME.snapSearchPage,
    lineofbusiness: LINE_OF_BUSINESS.snap,
  };
};

/**
 * Build analytics parameters for SNAP China disclaimer viewed event
 * @param params - China disclaimer viewed parameters
 * @returns Analytics parameters object
 */
export const buildSnapChinaDisclaimerViewedAnalytics = (
  params: SnapChinaDisclaimerViewedParams,
): Record<string, string | number> => {
  return {
    action: ACTIONS.VIEW,
    category: CHINA_DISCLAIMER_VIEWED.CATEGORY,
    label: params.disclaimerMessage,
    lineofbusiness: CHINA_DISCLAIMER_VIEWED.LINE_OF_BUSINESS,
    screenName: SCREEN_NAME.snapSearchPage,
  };
};

/**
 * Search Widget Event Analytics
 */

export interface AnalyticsParams {
  eventName: string;
  eventParams: Record<string, unknown>;
}

/**
 * Build analytics parameters for search widget event (native platform)
 * @param action - Event action string
 * @param payload - Event payload containing label and value
 * @returns Analytics parameters object
 */
export const buildSearchWidgetEventAnalyticsNative = (
  action: string,
  payload: { label?: string; value?: string },
): AnalyticsParams => {
  const params: Record<string, string> = {
    category: SNAP_SEARCH_WIDGET.CATEGORY,
    action,
    label: payload.label ?? '',
    lineofbusiness: SNAP_SEARCH_WIDGET.LINE_OF_BUSINESS,
    screenName: SNAP_SEARCH_WIDGET.SCREEN_NAME,
  };
  if (payload.value) {
    params.value = String(payload.value);
  }
  return {
    eventName: ACTIONS.TAP,
    eventParams: params,
  };
};

/**
 * Build analytics parameters for search widget event (web platform)
 * @param action - Event action string
 * @param payload - Event payload containing label and value
 * @returns Analytics parameters object
 */
export const buildSearchWidgetEventAnalyticsWeb = (
  action: string,
  payload: { label?: string; value?: string },
): AnalyticsParams => {
  const params: Record<string, string> = {
    'eventDetails.category': SNAP_SEARCH_WIDGET.CATEGORY,
    'eventDetails.action': ACTIONS.CLICK,
    'eventDetails.label': payload.label ?? '',
    lineofbusiness: SNAP_SEARCH_WIDGET.LINE_OF_BUSINESS,
    screenName: SNAP_SEARCH_WIDGET.SCREEN_NAME,
  };
  if (payload.value !== undefined || payload.value !== null) {
    params['eventDetails.value'] = String(payload.value);
  }
  return {
    eventName: ACTIONS.TRACK_EVENT_NO_ECOMMERCE,
    eventParams: params,
  };
};
